<script setup lang="ts">
/**
 * A control tile: one controllable thing on a dashboard wall — a light, a lock,
 * a blind, a sensor, a machine, a service. It knows nothing about what it
 * controls: the caller passes the texts and the state and reacts to the events.
 *
 * The head is the primary action: icon disc, `title`, optional `subtitle` and
 * the current `state` text („An“, „21,5 °C“, „Geschlossen“). It is one native
 * button (click, Enter, Space → `activate`); with `toggle` it reports `active`
 * as `aria-pressed`. When the action is not a plain switch — a door opener, a
 * blind that closes — `actionLabel` says what it does („Öffnen“): read after
 * the state and shown as the head's tooltip. `readonly` makes the head a plain
 * display (a sensor has nothing to switch).
 *
 * `active` (on / open / unlocked) fills the icon disc and washes the tile in
 * the accent: brand by default, the `tone` when one is set. A `tone` shows even
 * while inactive (an open window in `warning`). `loading` swaps the icon for a
 * spinner, sets `aria-busy` and ignores further activations while a command
 * runs. `disabled` (no permission) and `unavailable` (device unreachable) keep
 * the head focusable but inert (`aria-disabled`); `unavailable` replaces the
 * state with `unavailableLabel`. Neither disables the caller's controls — the
 * default slot receives `locked` for that.
 *
 * Secondary controls (a brightness slider, a cover position, a setpoint, an
 * extra button) go into the default slot, under the head and outside the
 * button. With `hasDetail` a small „more“ button emits `detail`, and so do a
 * long press and the context menu on the head — for opening a detail panel.
 *
 * Tiles take their `size` from an enclosing `NxControlTileGrid` unless they
 * set one themselves.
 */
import { computed, inject, onBeforeUnmount, ref, useSlots, type Component } from 'vue';
import Unplug from 'lucide-vue-next/dist/esm/icons/unplug.js';
import { LockClosedIcon, MoreHorizontalIcon } from '@blpde/nx-icons';
import { NxIconButton } from '../IconButton';
import { NxSpinner } from '../Loading';
import { accentVars } from '../Panel/accent';
import { controlTileGridKey, type NxControlTileSize, type NxControlTileTone } from './context';

const props = withDefaults(
  defineProps<{
    title: string;
    /** Secondary line, e.g. the room or the device type. */
    subtitle?: string;
    /** Icon component for the disc; the `#icon` slot replaces it. */
    icon?: Component;
    /** Current state as text, already formatted by the caller („An“, „21,5 °C“). */
    state?: string;
    /** On / open / unlocked: the accented look (and `aria-pressed` with `toggle`). */
    active?: boolean;
    /** The primary action switches between two states: reports `active` as `aria-pressed`. */
    toggle?: boolean;
    /**
     * What the primary action does („Öffnen“, „Schließen“), for an action the
     * title and state do not already say. Read after the state and shown as the
     * head's tooltip while the tile can be operated.
     */
    actionLabel?: string;
    tone?: NxControlTileTone;
    /** A command is running: spinner, `aria-busy`, activations are ignored. */
    loading?: boolean;
    /** Not operable, e.g. no permission. Stays readable and focusable. */
    disabled?: boolean;
    /** Shown on hover and read after the state while `disabled` („Keine Berechtigung“). */
    disabledReason?: string;
    /** The device is unreachable: dashed frame, `unavailableLabel` instead of the state. */
    unavailable?: boolean;
    /** No primary action: the head is a plain display (sensors). */
    readonly?: boolean;
    /** Shows the „more“ button; long press and context menu on the head emit `detail` too. */
    hasDetail?: boolean;
    /** Defaults to the enclosing grid's size, else `regular`. */
    size?: NxControlTileSize;
    unavailableLabel?: string;
    detailLabel?: string;
    loadingLabel?: string;
    /** Hold time (ms) of the long press that emits `detail`. */
    longPressDelay?: number;
  }>(),
  {
    subtitle: undefined,
    icon: undefined,
    state: undefined,
    active: false,
    toggle: false,
    actionLabel: undefined,
    tone: 'neutral',
    loading: false,
    disabled: false,
    disabledReason: undefined,
    unavailable: false,
    readonly: false,
    hasDetail: false,
    size: undefined,
    unavailableLabel: 'Nicht erreichbar',
    detailLabel: 'Details',
    loadingLabel: 'Wird ausgeführt …',
    longPressDelay: 500,
  }
);

const emit = defineEmits<{
  /** The primary action: click, Enter or Space on the head. */
  activate: [event: MouseEvent];
  /** Open the detail view: the „more“ button, a long press or the context menu. */
  detail: [];
}>();

defineSlots<{
  /** Replaces the `icon` prop inside the disc. */
  icon?(): unknown;
  /** Secondary controls under the head. `locked` = disabled or unavailable. */
  default?(props: { locked: boolean; active: boolean; loading: boolean }): unknown;
}>();

const grid = inject(controlTileGridKey, null);
const resolvedSize = computed<NxControlTileSize>(() => props.size ?? grid?.size.value ?? 'regular');

const locked = computed(() => props.disabled || props.unavailable);
const actionable = computed(() => !props.readonly && !locked.value && !props.loading);
const hasTone = computed(() => props.tone !== 'neutral');
const accentStyle = computed(() =>
  accentVars(hasTone.value ? props.tone : 'brand', undefined, '--tile-accent')
);
const slots = useSlots();
const hasDisc = computed(() => !!(props.icon || slots.icon || props.loading));
const stateText = computed(() => (props.unavailable ? props.unavailableLabel : props.state));
/** The action is said only while it can be run; a locked tile says why instead. */
const actionText = computed(() =>
  !props.readonly && !locked.value && props.actionLabel ? props.actionLabel : undefined
);

// ── Long press → detail ──────────────────────────────────────────────────
const MOVE_TOLERANCE = 10;
let pressTimer: ReturnType<typeof setTimeout> | undefined;
let pressOrigin: { x: number; y: number } | null = null;
/** A long press fired: swallow the click (and context menu) that follow it. */
const longPressed = ref(false);

function clearPress() {
  if (pressTimer !== undefined) clearTimeout(pressTimer);
  pressTimer = undefined;
  pressOrigin = null;
}

function onPointerdown(event: PointerEvent) {
  longPressed.value = false;
  if (!props.hasDetail || event.button !== 0) return;
  clearPress();
  pressOrigin = { x: event.clientX, y: event.clientY };
  pressTimer = setTimeout(() => {
    pressTimer = undefined;
    longPressed.value = true;
    emit('detail');
  }, props.longPressDelay);
}

function onPointermove(event: PointerEvent) {
  if (!pressOrigin) return;
  const dx = event.clientX - pressOrigin.x;
  const dy = event.clientY - pressOrigin.y;
  if (Math.hypot(dx, dy) > MOVE_TOLERANCE) clearPress();
}

function onContextmenu(event: MouseEvent) {
  if (!props.hasDetail) return;
  event.preventDefault();
  // Touch browsers raise the context menu for the same hold that already fired.
  if (longPressed.value) return;
  clearPress();
  emit('detail');
}

function onClick(event: MouseEvent) {
  if (longPressed.value) {
    longPressed.value = false;
    return;
  }
  if (!actionable.value) return;
  emit('activate', event);
}

onBeforeUnmount(clearPress);
</script>

<template>
  <div
    class="nx-control-tile"
    :class="[
      `size-${resolvedSize}`,
      hasTone ? `tone-${props.tone}` : undefined,
      {
        'has-tone': hasTone,
        'is-active': props.active && !props.unavailable,
        'is-loading': props.loading,
        'is-disabled': props.disabled,
        'is-unavailable': props.unavailable,
        'is-actionable': actionable,
        'is-readonly': props.readonly,
        'has-disc': hasDisc,
        'has-detail': props.hasDetail,
      },
    ]"
    :style="accentStyle"
    :title="props.disabled ? props.disabledReason : undefined"
    data-testid="nx-control-tile"
  >
    <div class="nx-control-tile__head">
      <component
        :is="props.readonly ? 'div' : 'button'"
        class="nx-control-tile__main"
        :type="props.readonly ? undefined : 'button'"
        :aria-pressed="!props.readonly && props.toggle ? props.active : undefined"
        :aria-disabled="!props.readonly && locked ? 'true' : undefined"
        :aria-busy="props.loading ? 'true' : undefined"
        :title="actionText"
        data-testid="nx-control-tile-main"
        @click="onClick"
        @pointerdown="onPointerdown"
        @pointermove="onPointermove"
        @pointerup="clearPress"
        @pointercancel="clearPress"
        @pointerleave="clearPress"
        @contextmenu="onContextmenu"
      >
        <span v-if="hasDisc" class="nx-control-tile__disc" aria-hidden="true">
          <NxSpinner v-if="props.loading" size="lg" />
          <slot v-else name="icon">
            <component :is="props.icon" class="nx-control-tile__icon" />
          </slot>
        </span>
        <span class="nx-control-tile__text">
          <span class="nx-control-tile__title" :title="props.title">{{ props.title }}</span>
          <span v-if="props.subtitle" class="nx-control-tile__subtitle">
            {{ props.subtitle }}
          </span>
          <span
            v-if="stateText || props.disabled"
            class="nx-control-tile__state"
            data-testid="nx-control-tile-state"
          >
            <Unplug
              v-if="props.unavailable"
              class="nx-control-tile__state-icon"
              aria-hidden="true"
            />
            <LockClosedIcon
              v-else-if="props.disabled"
              filled
              class="nx-control-tile__state-icon"
              aria-hidden="true"
            />
            <span class="nx-control-tile__state-text">{{ stateText }}</span>
            <span v-if="props.disabled && props.disabledReason" class="nx-control-tile__sr">
              {{ `· ${props.disabledReason}` }}
            </span>
          </span>
          <span v-if="actionText" class="nx-control-tile__sr" data-testid="nx-control-tile-action">
            {{ `· ${actionText}` }}
          </span>
        </span>
      </component>
      <NxIconButton
        v-if="props.hasDetail"
        class="nx-control-tile__detail"
        height="s"
        :aria-label="`${props.detailLabel}: ${props.title}`"
        :title="props.detailLabel"
        data-testid="nx-control-tile-detail"
        @click="emit('detail')"
      >
        <MoreHorizontalIcon filled />
      </NxIconButton>
    </div>

    <div v-if="$slots.default" class="nx-control-tile__controls">
      <slot :locked="locked" :active="props.active" :loading="props.loading" />
    </div>

    <span class="nx-control-tile__sr" aria-live="polite">
      {{ props.loading ? props.loadingLabel : '' }}
    </span>
  </div>
</template>

<style scoped>
/* --tile-accent* come from accentVars: the tone, else brand. They colour the
   disc, the state text and the wash; the neutral tile only uses them when
   active. */
.nx-control-tile {
  --tile-bg: var(--nxt-surface-raised-bg);
  --tile-edge: var(--nxt-border);
  --tile-disc-bg: color-mix(in srgb, var(--nxt-fg) 7%, var(--nxt-surface-raised-bg));
  --tile-disc-fg: var(--nxt-fg-subtle);
  --tile-state-fg: var(--nxt-fg);
  --tile-disc-size: var(--nxt-tile-size-md);

  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  min-width: 0;
  padding: var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--tile-edge);
  border-radius: var(--nxt-radius-xl);
  background: var(--tile-bg);
  box-shadow: var(--nxt-shadow-sm);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  transition:
    background-color var(--nxt-motion-base) var(--nxt-ease-in-out),
    border-color var(--nxt-motion-base) var(--nxt-ease-in-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out),
    transform var(--nxt-motion-fast) var(--nxt-ease-out);
}

/* Tone: the disc and the state speak in the tone even while inactive. The
   solid role colour carries text contrast in both themes; the subtle tokens
   are too faint to read as a tone on a tile. */
.nx-control-tile.has-tone {
  --tile-edge: color-mix(in srgb, var(--tile-accent-border) 70%, var(--nxt-border));
  --tile-disc-bg: color-mix(in srgb, var(--tile-accent) 16%, var(--nxt-surface-raised-bg));
  --tile-disc-fg: var(--tile-accent);
  --tile-state-fg: var(--tile-accent);
}

/* Active: a solid disc and a light wash of the accent over the whole tile. */
.nx-control-tile.is-active {
  --tile-bg: color-mix(in srgb, var(--tile-accent) 6%, var(--nxt-surface-raised-bg));
  --tile-edge: var(--tile-accent-border);
  --tile-disc-bg: var(--tile-accent);
  --tile-disc-fg: var(--tile-accent-on);
  --tile-state-fg: var(--tile-accent-fg);
}
.nx-control-tile.is-active.has-tone {
  --tile-state-fg: var(--tile-accent);
}

.nx-control-tile.is-actionable:hover {
  --tile-edge: var(--nxt-border-strong);
  box-shadow: var(--nxt-shadow-md);
}
.nx-control-tile.is-actionable.is-active:hover,
.nx-control-tile.is-actionable.has-tone:hover {
  --tile-edge: var(--tile-accent-border);
}
/* Tactile press on the head only, never while dragging a slider below. */
.nx-control-tile.is-actionable:has(.nx-control-tile__main:active) {
  transform: scale(0.98);
}

/* Disabled (no permission): readable, but plainly not operable. An active one
   keeps a quiet sign of it — the icon in the accent — but no accent frame,
   which would read as operable. */
.nx-control-tile.is-disabled {
  --tile-bg: var(--nxt-surface-sunken-bg);
  --tile-edge: var(--nxt-border);
  --tile-disc-bg: var(--nxt-disabled-bg);
  --tile-disc-fg: var(--nxt-disabled-fg);
  --tile-state-fg: var(--nxt-fg-muted);
  box-shadow: none;
}
.nx-control-tile.is-disabled.is-active {
  --tile-disc-fg: var(--tile-accent);
}
/* Unavailable (unreachable): a dashed frame and a struck-out look. */
.nx-control-tile.is-unavailable {
  --tile-bg: var(--nxt-surface-sunken-bg);
  --tile-edge: var(--nxt-border-strong);
  --tile-disc-bg: transparent;
  --tile-disc-fg: var(--nxt-disabled-fg);
  --tile-state-fg: var(--nxt-fg-muted);
  border-style: dashed;
  box-shadow: none;
}
.nx-control-tile.is-unavailable .nx-control-tile__disc {
  box-shadow: inset 0 0 0 var(--nxt-hairline) var(--nxt-border-strong);
}
.nx-control-tile.is-disabled .nx-control-tile__main,
.nx-control-tile.is-unavailable .nx-control-tile__main {
  cursor: not-allowed;
}
.nx-control-tile.is-loading .nx-control-tile__main {
  cursor: progress;
}

/* ── Head ─────────────────────────────────────────────────────────────── */
/* Regular: the disc on top, the texts under it at the full tile width; the
   „more“ button sits in the disc's row at the trailing edge. */
.nx-control-tile__head {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-control-tile__main {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--nxt-space-xs);
  min-width: 0;
  /* 44px hit target. */
  min-height: calc(var(--nxt-size-unit) * 2.75);
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: var(--nxt-radius-lg);
  background: none;
  color: inherit;
  font: inherit;
  text-align: start;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
button.nx-control-tile__main {
  cursor: pointer;
}
/* The long press must not select text or open the iOS callout. */
.nx-control-tile:not(.is-readonly) .nx-control-tile__main {
  user-select: none;
  -webkit-touch-callout: none;
}
/* The focus ring goes round the whole tile: the head is the tile's action,
   and a ring round the head alone would enclose the „more“ button. */
.nx-control-tile__main:focus-visible {
  outline: none;
}
.nx-control-tile:has(> .nx-control-tile__head > .nx-control-tile__main:focus-visible) {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}

.nx-control-tile__disc {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--tile-disc-size);
  height: var(--tile-disc-size);
  border-radius: var(--nxt-radius-full);
  background: var(--tile-disc-bg);
  color: var(--tile-disc-fg);
  font-size: var(--nxt-icon-size-lg);
  transition:
    background-color var(--nxt-motion-base) var(--nxt-ease-in-out),
    color var(--nxt-motion-base) var(--nxt-ease-in-out),
    transform var(--nxt-motion-base) var(--nxt-ease-out);
}
.nx-control-tile.is-active .nx-control-tile__disc {
  transform: scale(1.04);
}
.nx-control-tile__icon,
.nx-control-tile__disc :deep(svg) {
  width: 1em;
  height: 1em;
}

.nx-control-tile__text {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  min-width: 0;
}
/* Without a disc the title shares its row with the „more“ button. */
.nx-control-tile.has-detail:not(.has-disc) .nx-control-tile__text {
  padding-inline-end: var(--nxt-control-size-md);
}
.nx-control-tile__title {
  overflow: hidden;
  color: var(--nxt-heading-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-control-tile__subtitle {
  overflow: hidden;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-control-tile__state {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  margin-block-start: var(--nxt-space-2xs);
  color: var(--tile-state-fg);
  font-size: var(--nxt-font-size-md);
  font-variant-numeric: tabular-nums;
  font-weight: var(--nxt-font-weight-medium);
  line-height: var(--nxt-line-height-tight);
  transition: color var(--nxt-motion-base) var(--nxt-ease-in-out);
}
.nx-control-tile.is-active .nx-control-tile__state {
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-control-tile__state-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.nx-control-tile__state-icon {
  flex: none;
  width: var(--nxt-icon-size-sm);
  height: var(--nxt-icon-size-sm);
}
/* Two classes: NxButton's own `button.width-icon-only` sets a position too. */
.nx-control-tile__head > .nx-control-tile__detail {
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  color: var(--nxt-fg-muted);
}

.nx-control-tile__controls {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  /* Controls sit at the bottom, so tiles of one grid row line them up. */
  margin-block-start: auto;
  min-width: 0;
}

/* ── Compact ──────────────────────────────────────────────────────────── */
.nx-control-tile.size-compact {
  --tile-disc-size: var(--nxt-tile-size-sm);
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-radius: var(--nxt-radius-lg);
}
.nx-control-tile.size-compact .nx-control-tile__main {
  flex-direction: row;
  align-items: center;
  gap: var(--nxt-space-sm);
}
.nx-control-tile.size-compact .nx-control-tile__text {
  flex: 1;
  align-self: auto;
  padding-inline-end: 0;
}
/* In the row, not over it — but still `relative`: NxButton centres its icon
   absolutely inside the button, and `static` would centre it in the head. */
.nx-control-tile.size-compact .nx-control-tile__detail {
  position: relative;
  inset: auto;
  flex: none;
  align-self: center;
}
.nx-control-tile.size-compact .nx-control-tile__disc {
  font-size: var(--nxt-icon-size-md);
}
.nx-control-tile.size-compact .nx-control-tile__state-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-control-tile.size-compact .nx-control-tile__state {
  margin-block-start: 0;
  font-size: var(--nxt-font-size-sm);
}

/* Screen-reader-only text (the disabled reason, the loading announcement). */
.nx-control-tile__sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .nx-control-tile,
  .nx-control-tile__disc,
  .nx-control-tile__state {
    transition: none;
  }
  .nx-control-tile.is-actionable:has(.nx-control-tile__main:active),
  .nx-control-tile.is-active .nx-control-tile__disc {
    transform: none;
  }
}
@media (forced-colors: active) {
  .nx-control-tile.is-active .nx-control-tile__disc {
    outline: calc(var(--nxt-hairline) * 2) solid Highlight;
  }
  .nx-control-tile:has(> .nx-control-tile__head > .nx-control-tile__main:focus-visible) {
    outline-color: Highlight;
  }
}
</style>
